import { Card, SortOptionType } from '@/types/srp'
import { useMemo } from 'react'
import { isEmpty } from '@/utils/general/browserHelper'
import { isCheckInBaggageEnabled } from '@/utils/srp'
import { useAppSelector } from '@/redux/store/hooks'
import { getWowSorting } from '@/redux/slices/srpSlice'

type useSortedCardsProps = {
    isSearchApiLoading: boolean
    activeSort: SortOptionType[]
    filteredCards: Card[][]
    sortData: Record<string, any>
    processedData: any
    selectedFilters: any
}

const useSortedCards = ({ isSearchApiLoading, activeSort, filteredCards, sortData, processedData, selectedFilters }: useSortedCardsProps) => {
    const wowSorting = useAppSelector(getWowSorting)
    const { ow: showCheckInBaggagePriceOW, rt: showCheckInBaggagePriceRT } = isCheckInBaggageEnabled(selectedFilters)
    const { onwardLeg: onwardLegFilters, returnLeg: returnLegFilters } = selectedFilters || {}
    const { ptcFare: onwardPtcFare } = onwardLegFilters || {}
    const { ptcFare: returnPtcFare } = returnLegFilters || {}
    const indexToJourneyMap: { [key: number]: 'J1' | 'J2' } = {
        0: 'J1',
        1: 'J2'
    }

    const getSortedCardsIdList = ({
        filteredCards,
        activeSort
    }: {
        filteredCards: Card[][]
        activeSort: SortOptionType[]
    }) => {
        if (isSearchApiLoading || isEmpty(filteredCards)) {
            return []
        }
        const toMinutes = (t: any): number => {
            if (!t || typeof t !== 'string') return -1
            const match = String(t).match(/(\d{1,2}):(\d{2})/)
            if (!match) return -1
            const hh = parseInt(match[1], 10)
            const mm = parseInt(match[2], 10)
            if (isNaN(hh) || isNaN(mm)) return -1
            return hh * 60 + mm
        }
        return activeSort.map((activeSortOption, index) => {
            let { sortKey } = activeSortOption
            const showCheckInBaggagePrice = index === 0 ? showCheckInBaggagePriceOW : showCheckInBaggagePriceRT
            const isPtcFilterApplied = index === 0 ? onwardPtcFare?.checked : returnPtcFare?.checked
            //const sortedCardsIdList = sortData[sortKey]?.[indexToJourneyMap[index]]
            // total price: keep current filtered order
            if (sortKey === 'total_price_up') {
                const currentLegCards = filteredCards[index] || []
                return currentLegCards.map((card: any) => card?.cardId)
            }
            if (showCheckInBaggagePrice) {
                if (sortKey === 'price_up') {
                    sortKey = 'check_in_baggage_price_up'
                } else if (sortKey === 'price_down') {
                    sortKey = 'check_in_baggage_price_down'
                }   
            }
            if (isPtcFilterApplied) {
                if (sortKey === 'price_up') {
                    sortKey = 'ptc_fare_price_up'
                } else if (sortKey === 'price_down') {
                    sortKey = 'ptc_fare_price_down'
                }
            }
            // arrival: prefer server; fallback to client-side by lastArrival time with tie-breakers
            // if (sortKey === 'arrival_up' || sortKey === 'arrival_down') {
            //     const serverSorted = sortData?.[sortKey]?.[indexToJourneyMap[index]]
            //     if (Array.isArray(serverSorted) && serverSorted.length > 0) {
            //         return serverSorted
            //     }
            //     const cards = filteredCards?.[index] || []
            //     const sorted = [...cards].sort((a: any, b: any) => {
            //         const arrA = toMinutes(a?.summary?.lastArrival?.airport?.time)
            //         const arrB = toMinutes(b?.summary?.lastArrival?.airport?.time)
            //         if (arrA !== arrB) return sortKey === 'arrival_up' ? arrA - arrB : arrB - arrA
            //         const depA = toMinutes(a?.summary?.firstDeparture?.airport?.time)
            //         const depB = toMinutes(b?.summary?.firstDeparture?.airport?.time)
            //         if (depA !== depB) return sortKey === 'arrival_up' ? depA - depB : depB - depA
            //         return (a?.cardId || 0) - (b?.cardId || 0)
            //     })
            //     return sorted.map((card: any) => card?.cardId)
            // }
            // if(sortKey === 'round_trip_price_up'){
            //     const rtList = sortData?.round_trip_price?.[indexToJourneyMap[index]]
            //     if(Array.isArray(rtList) && rtList.length > 0){
            //         return rtList
            //     }
            //     const priceUpList = sortData?.price_up?.[indexToJourneyMap[index]]
            //     return Array.isArray(priceUpList) && priceUpList.length > 0 ? priceUpList : []
            // }
            // // round trip price: fallback to price_up if server key absent
            // const effectiveSortKey = sortKey === 'round_trip_price_up' ? 'price_up' : sortKey
            // WOW price sort — use the wowSorting array returned by the
            // /flight/v2/fetch-dynamic-content response (keyed by journey).
            // Falls back to price_up if wowSorting hasn't loaded yet so the
            // list isn't empty mid-fetch.
            if (sortKey === 'wow_price_up') {
                const wowList = wowSorting?.[indexToJourneyMap[index]]
                if (Array.isArray(wowList) && wowList.length > 0) {
                    return wowList
                }
                return sortData['price_up']?.[indexToJourneyMap[index]] || []
            }
            const sortedCardsIdList = sortData[sortKey]?.[indexToJourneyMap[index]] || []
            return sortedCardsIdList
        })
    }
    const removeSamePriceCards = (cards = []) => {
        let samePriceCards: any[] = []
        const filteredResponse = cards?.filter((card: any = {}) => {
            if (isEmpty(card)) {
                return false
            }
            const isSamePriceCard = samePriceCards.includes(card?.cardId)
            if (!isSamePriceCard && card?.samePriceCards?.length > 0) {
                samePriceCards.push(...card?.samePriceCards)
            }
            return !isSamePriceCard
        })
        return filteredResponse
    }

    const memoResult = useMemo(() => {
        if (isSearchApiLoading) {
            return { sortedCards: [], unModifiedCards: [] }
        }
        let unModifiedSortedFilteredCards: any[] = []
        const sortedCardsIdList = getSortedCardsIdList({ filteredCards, activeSort })
        const sortedFilteredCards = sortedCardsIdList.map((cardIdList, index) => {
            if (isEmpty(cardIdList)) {
                return []
            }
            let cardsArrayMap: { [key: string]: any } = {}
            let cardsArray = cardIdList.map((cardId: any) => {
                if (isEmpty(filteredCards[index])) {
                    return {}
                } else {
                    const card = filteredCards[index].find((card: { cardId: any }) => card.cardId == cardId)
                    if (card && card.cardId != null) {
                        cardsArrayMap[card.cardId] = card
                    }
                    if (card?.travelOptionId) {
                        cardsArrayMap[card.travelOptionId] = card
                    }
                    return card
                }
            })
            unModifiedSortedFilteredCards[index] = cardsArrayMap
            cardsArray = processedData?.isIntl && processedData?.tripType === 'oneway'
                ? removeSamePriceCards(cardsArray)
                : cardsArray.filter((card: object) => !isEmpty(card))
            return cardsArray
        })
        return {sortedCards: sortedFilteredCards, unModifiedCards: unModifiedSortedFilteredCards}
    }, [activeSort, filteredCards, isSearchApiLoading, processedData, sortData, selectedFilters,wowSorting ])

    return memoResult
}

export default useSortedCards
